/**
 * Quality Module — shared format-aware quality selector for all tools.
 *
 * For LOSSY formats (MP3 / AAC / Opus / OGG / WMA) it offers kbps choices
 * and produces bitrate args (-b:a 192k or VBR -q:a N depending on the
 * codec — delegated to FFmpegProcessor.getCodecArgs which already maps
 * VBR per format).
 *
 * For LOSSLESS formats (WAV / FLAC / AIFF) the kbps idea is meaningless:
 * size is fully determined by sample rate × bit depth × channels. Pro
 * audio tools (Audacity, Ableton, Logic, Adobe Audition, Reaper) all
 * offer a BIT DEPTH choice instead — 16 / 24 / 32-bit float — and that
 * choice is the actual user-facing knob for lossless quality.
 *
 * This module collapses both into a single format-aware <select> that
 * swaps option set + label automatically when the format selector
 * changes. Tools just include the Blade partial and call init().
 *
 * Usage in Blade:
 *   @include('components.quality-select', ['tool' => 'audio-enhancer'])
 *
 * Usage in JS (after FFmpegProcessor + format-select are present):
 *   QualityModule.init({ tool: 'audio-enhancer', formatId: 'format-select' });
 *
 * In FFmpeg arg construction:
 *   var args = ['-vn', '-af', filterChain].concat(QualityModule.getArgs(ext));
 *
 * Drop-in replacement for FFmpegProcessor.getCodecArgs(ext, bitrate) — it
 * delegates for lossy/source, layers bit-depth override for lossless.
 */
(function(){
'use strict';

// Localized labels — embedded inline so the module works on every page
// without per-tool translation wiring. Pattern matches ChannelModule.
var LABELS = {
    quality:    { en: 'Quality',     ru: 'Качество',     ko: '품질',         ar: 'الجودة',         pt: 'Qualidade',  id: 'Kualitas',  de: 'Qualität',   es: 'Calidad',              fr: 'Qualité', uk: 'Якість', tr: 'Kalite' },
    bit_depth:  { en: 'Bit depth',   ru: 'Разрядность',  ko: '비트 깊이',    ar: 'عمق البت',       pt: 'Profundidade de bits', id: 'Kedalaman bit', de: 'Bittiefe', es: 'Profundidad de bits', fr: 'Profondeur de bits', uk: 'Розрядність', tr: 'Bit derinliği' },
    source:     { en: 'Source',      ru: 'Исходное',     ko: '원본',         ar: 'المصدر',         pt: 'Original',   id: 'Asli',      de: 'Original',   es: 'Original',             fr: 'Original', uk: 'Вихідне', tr: 'Kaynak' },
    bit_n:      { en: ':n-bit',      ru: ':n-бит',       ko: ':n비트',       ar: ':n بت',          pt: ':n bits',    id: ':n bit',    de: ':n Bit',     es: ':n bits',              fr: ':n bits', uk: ':n-біт', tr: ':n bit' },
    bit_n_float:{ en: ':n-bit float', ru: ':n-бит float', ko: ':n비트 float', ar: ':n بت float',    pt: ':n bits float', id: ':n bit float', de: ':n Bit float', es: ':n bits float',  fr: ':n bits float', uk: ':n-біт float', tr: ':n bit float' },
    /* Подсказка под КОНТРОЛЕМ, а не под форматом: список меняет смысл вместе с
     * форматом вывода, и подсказка обязана меняться с ним. На /convert она была
     * прибита в блейде («128 — хорошо для речи/подкастов…») и оставалась про
     * kbps, когда список уже показывал биты — рядом с меткой «Битрейт» над
     * «16-бит / 24-бит / 32-бит float». */
    depth_tip: {
        en: '16-bit — CD standard, most compatible. 24-bit — headroom for further editing. 32-bit float — no rounding.',
        ru: '16 бит — стандарт CD, самый совместимый. 24 бита — запас для дальнейшей обработки. 32 бита float — без округления.',
        ko: '16비트 — CD 표준, 호환성 최고. 24비트 — 추가 편집을 위한 여유. 32비트 float — 반올림 없음.',
        ar: '16 بت — معيار CD والأكثر توافقًا. 24 بت — هامش للتحرير اللاحق. 32 بت float — بلا تقريب.',
        pt: '16 bits — padrão de CD, mais compatível. 24 bits — margem para edição posterior. 32 bits float — sem arredondamento.',
        id: '16 bit — standar CD, paling kompatibel. 24 bit — ruang untuk penyuntingan lanjutan. 32 bit float — tanpa pembulatan.',
        de: '16 Bit — CD-Standard, am kompatibelsten. 24 Bit — Reserve für weitere Bearbeitung. 32 Bit Float — ohne Rundung.',
        es: '16 bits — estándar de CD, el más compatible. 24 bits — margen para edición posterior. 32 bits float — sin redondeo.',
        fr: '16 bits — standard CD, le plus compatible. 24 bits — marge pour l’édition ultérieure. 32 bits float — sans arrondi.',
        uk: '16 біт — стандарт CD, найсумісніший. 24 біти — запас для подальшої обробки. 32 біти float — без округлення.',
        tr: '16 bit — CD standardı, en uyumlu. 24 bit — sonraki düzenleme için pay. 32 bit float — yuvarlama yok.'
    }
};

var DEFAULT_LOSSY_KBPS    = [128, 192, 256, 320];
var DEFAULT_LOSSLESS_BITS = [16, 24, 32]; // 32-bit = float for WAV
var FLAC_BITS             = [16, 24];      // FLAC v1.4 caps at 24-bit

// Locale resolved once at module init; tools that bind later still see it
// via window.__LOCALE because the patch landed before any tool runs.
var _locale = (function(){
    var loc = (window.__LOCALE || document.documentElement.lang || 'en').toString();
    return loc.substring(0, 2).toLowerCase();
})();

function tr(key, replacements) {
    var entry = LABELS[key];
    if (!entry) return key;
    var s = entry[_locale] || entry.en || key;
    if (replacements) {
        Object.keys(replacements).forEach(function(k){
            s = s.replace(':' + k, replacements[k]);
        });
    }
    return s;
}

function isLossless(ext) {
    ext = (ext || '').toString().toLowerCase();
    // CAUTION: FFmpegProcessor's __sharedModule stub returns FALSE for
    // isLossless as a "safe default" (per the timbrica-core.js comment about
    // bitrate-UI gating in /denoise). We must explicitly skip the stub —
    // otherwise on tools that don't __ensureFFmpeg early (convert, dictaphone)
    // the stub's `false` would silently turn WAV/FLAC into "lossy" here, which
    // makes argsFor/populateOptions return kbps options instead of bit-depth.
    if (window.FFmpegProcessor && !window.FFmpegProcessor.__isStub
        && typeof window.FFmpegProcessor.isLossless === 'function') {
        return window.FFmpegProcessor.isLossless(ext);
    }
    // Fallback membership matches FFmpegProcessor's real list. Adjust here if
    // ffmpeg-processor.js's CODECS-lossless list ever grows.
    return ['wav', 'flac', 'aiff', 'aif'].indexOf(ext) !== -1;
}

function isFlac(ext) {
    return (ext || '').toString().toLowerCase() === 'flac';
}

var _selectEl = null;
var _fieldEl = null;
var _labelEl = null;
var _formatEl = null;        // when caller has a <select> for format
var _formatGetter = null;    // when caller has buttons / radio / custom (returns string)
var _toolName = '';
var _preferSource = false;   // tools that opt in via init({preferSource:true}) default to "source"
var _config = {
    lossyOptions:    DEFAULT_LOSSY_KBPS.slice(),
    losslessOptions: DEFAULT_LOSSLESS_BITS.slice(),
    flacOptions:     FLAC_BITS.slice()
};

function currentFormat() {
    if (_formatGetter) {
        try { return (_formatGetter() || '').toString().toLowerCase(); }
        catch(_) { return ''; }
    }
    return _formatEl ? (_formatEl.value || '').toString().toLowerCase() : '';
}
// Source-file detection: bit-depth for WAV/FLAC, bitrate for lossy. When the
// user keeps the "source" option, getArgs() uses these values to produce
// codec args that match the input — pro-audio эталон ("give me back what I
// gave you" instead of always defaulting to 16-bit / 192 kbps).
var _detectedSource = { bitDepth: null, bitrate: null, isFloat: false };
/* The last time "source" quality had to be lowered to this tier's ceiling —
   {kind, src, out, top, text} or null. Read by the tool AFTER the export, so it
   can show the sentence where the user is actually looking. */
var _lastSourceClamp = null;
/* One door for both halves of the debt clamp() names: record the fact for the
   tool that wants to place the sentence itself, and let the primitive publish
   it into the shared limit block for every tool that does not. Called on EVERY
   source export, including the ones that lower nothing — that call is what
   retracts a row left over from the previous file. */
function _discloseSourceClamp(bitrate) {
    try {
        _lastSourceClamp = (window.__qualityTier && typeof window.__qualityTier.sourceClamped === 'function')
            ? window.__qualityTier.sourceClamped('kbps', bitrate, { render: !_ownsClampNotice })
            : null;
    } catch (_) { _lastSourceClamp = null; }
}
/* A tool that shows the sentence itself sets this, so the shared block does not
   repeat it (repair-audio prints it under the bitrate row it explains). */
var _ownsClampNotice = false;
// Highest bit depth THIS tool's export path can actually produce. 0 = no
// limit (the tool re-encodes the original file, or its writer handles any
// depth). Set per tool via init({maxWriterBits}) — see resolvedBits().
var _maxWriterBits = 0;

// WAV header parser. Reads as little as 64 bytes from the file head, walks
// chunks until it finds 'fmt ', extracts bitsPerSample + format. Handles
// extended fmt chunks (BWF, WAVEFORMATEXTENSIBLE) by walking past whatever
// length the chunk actually claims, not assuming offset 16.
async function probeWavHeader(file) {
    try {
        var buf = await file.slice(0, 96).arrayBuffer();
        if (buf.byteLength < 36) return null;
        var dv = new DataView(buf);
        // 'RIFF' at 0, 'WAVE' at 8
        if (dv.getUint32(0, false) !== 0x52494646) return null;
        if (dv.getUint32(8, false) !== 0x57415645) return null;
        var pos = 12;
        while (pos + 8 <= buf.byteLength) {
            var id = dv.getUint32(pos, false);
            var size = dv.getUint32(pos + 4, true);
            if (id === 0x666d7420) { // 'fmt '
                if (pos + 24 > buf.byteLength) return null;
                var format = dv.getUint16(pos + 8, true);   // 1=PCM, 3=IEEE float, 0xFFFE=extensible
                var bits   = dv.getUint16(pos + 22, true);  // bitsPerSample
                if (!bits || bits > 64) return null;
                return { bitDepth: bits, isFloat: (format === 3) };
            }
            pos += 8 + size;
            if (size === 0 || pos > 4096) return null;
        }
        return null;
    } catch(_) { return null; }
}

// FLAC STREAMINFO parser. The first metadata block after the 'fLaC'
// signature is mandatorily STREAMINFO — its bits_per_sample field sits at
// bits 36-40 of the 34-byte block (which starts after a 4-byte header).
// Stored value is bits-1, so a 24-bit FLAC has stored value 23.
async function probeFlacHeader(file) {
    try {
        var buf = await file.slice(0, 64).arrayBuffer();
        if (buf.byteLength < 26) return null;
        var u8 = new Uint8Array(buf);
        // 'fLaC' signature = 0x66 0x4c 0x61 0x43
        if (u8[0] !== 0x66 || u8[1] !== 0x4c || u8[2] !== 0x61 || u8[3] !== 0x43) return null;
        // Layout: 4B 'fLaC' + 4B block header + 34B STREAMINFO body
        //   bytes 8-9   : min_block_size
        //   bytes 10-11 : max_block_size
        //   bytes 12-14 : min_frame_size
        //   bytes 15-17 : max_frame_size
        //   bytes 18-21 : packed { 20b sample_rate, 3b channels, 5b bits-1, 4b samples_high }
        //   bytes 22-25 : remaining 32 bits of total_samples
        //   bytes 26-41 : MD5
        var packed = (u8[18] << 24) | (u8[19] << 16) | (u8[20] << 8) | u8[21];
        // bits_per_sample-1 sits at bit positions 23..27 (counting MSB=0)
        // packed >>> 4 shifts away the top 4 bits of total_samples_high,
        // leaving bits_per_sample-1 in the low 5 bits.
        var bps = ((packed >>> 4) & 0x1F) + 1;
        if (bps < 4 || bps > 32) return null;
        return { bitDepth: bps, isFloat: false };
    } catch(_) { return null; }
}

async function detectSource(file, opts) {
    opts = opts || {};
    _detectedSource = { bitDepth: null, bitrate: null, isFloat: false };
    /* Another file: the sentence about the previous one no longer stands. Its
       disclosure is withdrawn here — with `render:false` (the tool prints it under
       its own row) the shared block withholds its general «your plan exports X»
       line while a disclosure is out, and it would stay hidden until the next
       export (quality-tier.js, «one fact, one sentence», 2026-10-08). */
    if (_lastSourceClamp) _discloseSourceClamp(0);
    if (!file || !file.size) {
        if (_selectEl) updateSourceLabel();
        return _detectedSource;
    }
    var ext = ((file.name || '').split('.').pop() || '').toLowerCase();
    if (ext === 'wav' || ext === 'wave') {
        var w = await probeWavHeader(file);
        if (w) { _detectedSource.bitDepth = w.bitDepth; _detectedSource.isFloat = !!w.isFloat; }
    } else if (ext === 'flac') {
        var f = await probeFlacHeader(file);
        if (f) { _detectedSource.bitDepth = f.bitDepth; }
    } else if (opts.bitrate && opts.bitrate > 0) {
        // Explicit source bitrate from a caller that already probed the file
        // (e.g. repair-audio runs FFmpegProcessor.probe → "bitrate: 192 kb/s").
        // The exact declared value beats the size/duration estimate below —
        // and, crucially, it's available even when the caller has NO duration
        // yet (the estimate branch is duration-gated). Closes the deep-mode
        // 192→128 downgrade: feedback 2026-07-18 ea5a887b, a 192 kbps MP3
        // re-encoded to 128 because 'source' had no detected bitrate and fell
        // to libmp3lame's default. Cap at 320 (FFmpeg VBR ceiling).
        _detectedSource.bitrate = Math.min(opts.bitrate, 320);
    } else if (opts.duration && file.size) {
        // Lossy: estimate from payload size / duration. Embedded cover art
        // inflates a naive size-based estimate badly (measured 2026-07-16:
        // a 128 kbps file with a ~520 KB cover detected as "198 kbps" and
        // re-encoded at 192 — 1.5× bloat, mislabeled). Subtract the ID3v2
        // tag (header: 'ID3' + syncsafe 4-byte length at bytes 6-9), which
        // is where covers live in mp3. Cap at 320 — common encoders run
        // 128-320 kbps and FFmpeg doesn't accept higher VBR settings
        // cleanly across formats.
        var payload = file.size;
        try {
            var idHead = new Uint8Array(await file.slice(0, 10).arrayBuffer());
            if (idHead.length === 10 && idHead[0] === 0x49 && idHead[1] === 0x44 && idHead[2] === 0x33) {
                var tagLen = ((idHead[6] & 0x7f) << 21) | ((idHead[7] & 0x7f) << 14)
                           | ((idHead[8] & 0x7f) << 7) | (idHead[9] & 0x7f);
                if (tagLen > 0 && tagLen + 10 < file.size) payload = file.size - tagLen - 10;
            }
        } catch (_) {}
        _detectedSource.bitrate = Math.min(Math.round(payload * 8 / opts.duration / 1000), 320);
    }
    if (_selectEl) updateSourceLabel();
    return _detectedSource;
}

// Refresh the "source" option's label so the user can see what was
// detected. "Исходное · 24-bit" / "Source · 192 kbps" / "Original" if
// nothing detected. Called automatically after detectSource() and after
// every format change (because the right unit depends on output format).
function updateSourceLabel() {
    if (!_selectEl) return;
    var srcOpt = _selectEl.querySelector('option[value="source"]');
    if (!srcOpt) return;
    var ext = currentFormat();
    var lossless = isLossless(ext);
    var label = tr('source');
    if (lossless && _detectedSource.bitDepth) {
        var b = _detectedSource.bitDepth;
        var floatish = (b >= 32 && _detectedSource.isFloat);
        label += ' · ' + tr(floatish ? 'bit_n_float' : 'bit_n', { n: b });
    } else if (!lossless && _detectedSource.bitrate) {
        label += ' · ' + _detectedSource.bitrate + ' kbps';
    }
    srcOpt.textContent = label;
}

function buildOptionsForFormat(ext) {
    if (isLossless(ext)) {
        var bits = isFlac(ext) ? _config.flacOptions : _config.losslessOptions;
        return bits.map(function(b){
            return {
                value: String(b),
                label: tr(b === 32 ? 'bit_n_float' : 'bit_n', { n: b })
            };
        });
    }
    return _config.lossyOptions.map(function(k){
        return { value: String(k), label: k + ' kbps' };
    });
}

// Repopulate options when format changes. Tries to preserve the user's
// current selection; if invalid for the new format, falls back to a
// sensible default (16-bit for lossless, 192 kbps for lossy).
function repopulate() {
    if (!_selectEl) return;
    var ext = currentFormat();
    var lossless = isLossless(ext);
    var options = buildOptionsForFormat(ext);
    var prev = _selectEl.value;

    // Build new option set. "Source" is always first — it means
    // "best-effort match the input".
    var html = '<option value="source">' + tr('source') + '</option>';
    options.forEach(function(o){
        html += '<option value="' + o.value + '">' + o.label + '</option>';
    });
    _selectEl.innerHTML = html;

    // Update label text (Quality vs Bit depth)
    if (_labelEl) {
        _labelEl.textContent = lossless ? tr('bit_depth') : tr('quality');
    }

    var validValues = ['source'].concat(options.map(function(o){return o.value}));
    if (validValues.indexOf(prev) !== -1) {
        _selectEl.value = prev;
    } else if (_preferSource) {
        // Tools that opt in via init({ preferSource: true }) default to "source"
        // so re-encode preserves input bitrate / bit-depth. /repair-audio uses
        // this — pre-fix Spain user 92.187.149.103 2026-05-19 uploaded a 320 kbps
        // MP3, our default 192 dropped him to 192. "Suena igual" complaint
        // upstream was actually "sounds worse" — source default closes that.
        _selectEl.value = 'source';
    } else if (lossless) {
        _selectEl.value = '16'; // CD-standard, smallest, most compatible
    } else {
        _selectEl.value = '192'; // common default across DAW exports
    }

    // Refresh the "source" option's label — its display depends on the
    // OUTPUT format (lossless = bit-depth, lossy = kbps), so we have to
    // re-render every time the format changes.
    updateSourceLabel();

    // Close the options this plan can't reach. MUST run after the options are
    // rebuilt and after the value is restored — repopulate() blows away the
    // whole <select>, so any earlier gating is gone by now. The kind switches
    // with the format: kbps for lossy, bit-depth for lossless.
    //
    // "source" is deliberately NEVER gated (quality-tier.js skips options with
    // no comparable number): it means "match the input", which cannot exceed
    // what the user already brought us. A free user re-encoding their own
    // 320 kbps file at "source" keeps 320 — we only cap what WE would add.
    applyQualityTier();

    // Notify listeners so ChannelModule, etc., can react if needed
    try { _selectEl.dispatchEvent(new Event('change', { bubbles: true })); } catch(_){}
}

/**
 * Gate the select against the plan's export ceiling (window.__qualityTier).
 * No-op when the primitive is absent (older cached page) or the tier snapshot
 * hasn't landed — it fails open by design, so a broken ceiling never bricks
 * the tool. Re-runs on tier-bootstrap:ready via the listener at init().
 */
function applyQualityTier() {
    if (!_selectEl || !window.__qualityTier) return;
    try {
        var kind = isLossless(currentFormat()) ? 'bits' : 'kbps';
        // See the note in populateOptions: the attribute lets the shared late
        // passes re-gate this select with the current kind.
        _selectEl.setAttribute('data-quality-gate', kind);
        window.__qualityTier.gate(_selectEl, kind);
    } catch (_) {}
}

function getArgs(ext) {
    var fp = window.FFmpegProcessor;
    // Defensive fallback: when init() hasn't wired the select yet (race
    // with FFmpegProcessor lazy load), behave exactly like the legacy
    // getCodecArgs(ext) call — no-bitrate codec defaults. Tools that call
    // QualityModule.getArgs(ext) before init can rely on this instead of
    // gating every call on a "is module ready?" check.
    if (!_selectEl) {
        return fp && fp.getCodecArgs ? fp.getCodecArgs(ext) : [];
    }
    var value = _selectEl.value;
    var isSource = (value === 'source' || !value);

    if (isLossless(ext)) {
        // ONE resolution path. This branch used to re-derive the depth itself
        // (source depth → plan clamp) in parallel with resolvedBits(), and the
        // two drifted: a tool asked resolvedBits() to size its WAV writer (got
        // 16) while getArgs() told FFmpeg s32/24-bit. Measured on
        // /vocal-remover: every sample's low byte was zero — 16-bit data in a
        // 24-bit wrapper, i.e. a label. resolvedBits() is now the single answer,
        // and it also enforces the plan ceiling and the writer ceiling.
        return getBitDepthArgs(ext, resolvedBits(ext, value));
    }

    // Lossy export. When user picked "source" and we detected an input
    // bitrate (lossy source), preserve that — but never above the ceiling.
    if (isSource) {
        var ceil = audioKbpsCeiling();       // Infinity for Pro / no-ceiling
        if (_detectedSource.bitrate && fp && fp.getCodecArgs) {
            /* The clamp is deliberate (Farid 2026-07-19, "cap source too": keeping
               the source bitrate is a paid axis). What was missing is the other
               half of that decision — the user being TOLD, with their own numbers,
               at the moment their 193 kbps file comes back at 129. __qualityTier
               records the fact (and emits the first sensor this class ever had);
               the tool decides where to say it. */
            _discloseSourceClamp(_detectedSource.bitrate);
            return fp.getCodecArgs(ext, Math.min(_detectedSource.bitrate, ceil));
        }
        /* Source picked, but its bitrate is unknown (no probe, lossless input).
           Nothing is being lowered, so anything the previous export published
           has to come down — otherwise the row outlives the file it described. */
        _discloseSourceClamp(0);
        // Input bitrate unknown: a free ceiling still applies, so encode AT the
        // ceiling rather than letting the codec pick its (higher) default.
        if (ceil !== Infinity && fp && fp.getCodecArgs) return fp.getCodecArgs(ext, ceil);
        return fp && fp.getCodecArgs ? fp.getCodecArgs(ext) : [];
    }
    var kbps = parseInt(value, 10);
    if (isNaN(kbps) || kbps <= 0) {
        return fp && fp.getCodecArgs ? fp.getCodecArgs(ext) : [];
    }
    // Defense in depth: gate() already disables >ceiling options, but a persisted
    // value / devtools / a stale cached page could still deliver one here.
    kbps = capKbps(kbps);
    return fp && fp.getCodecArgs ? fp.getCodecArgs(ext, kbps) : ['-b:a', kbps + 'k'];
}

// Plan ceilings, resolved live from window.__qualityTier. Infinity when the
// primitive is absent, the tier hasn't landed, or the user is Pro — i.e. exactly
// the cases where nothing should be capped (fail-open).
function audioKbpsCeiling() {
    return (window.__qualityTier && !window.__qualityTier.isPro())
        ? window.__qualityTier.audioMaxKbps() : Infinity;
}
function capKbps(k) {
    return (window.__qualityTier) ? window.__qualityTier.clamp('kbps', k) : k;
}
function capBits(b) {
    return (window.__qualityTier) ? window.__qualityTier.clamp('bits', b) : b;
}

function getBitDepthArgs(ext, bits) {
    ext = (ext || '').toLowerCase();
    if (ext === 'wav') {
        // 32-bit always emits float — matches the UI label "32-bit float".
        // FFmpeg's pcm_f32le accepts both int32 and float32 input, so this
        // works for all source paths.
        if (bits >= 32) return ['-c:a', 'pcm_f32le'];
        if (bits >= 24) return ['-c:a', 'pcm_s24le'];
        return ['-c:a', 'pcm_s16le'];
    }
    if (ext === 'flac') {
        // FLAC's sample_fmt: s16 → 16-bit, s32 → up to 32-bit container.
        // bits_per_raw_sample tells decoders the meaningful bit depth.
        if (bits >= 24) return ['-c:a', 'flac', '-sample_fmt', 's32', '-bits_per_raw_sample', '24'];
        return ['-c:a', 'flac', '-sample_fmt', 's16'];
    }
    if (ext === 'aiff' || ext === 'aif') {
        if (bits >= 24) return ['-c:a', 'pcm_s24be'];
        return ['-c:a', 'pcm_s16be'];
    }
    // Unknown lossless container — let FFmpegProcessor fall through with
    // its built-in codec defaults.
    return [];
}

function getCurrentValue() {
    return _selectEl ? _selectEl.value : null;
}

function init(opts) {
    opts = opts || {};
    _selectEl = document.getElementById(opts.qualityId || 'quality-select');
    _fieldEl  = document.getElementById(opts.fieldId  || 'quality-field');
    _formatEl = opts.formatId ? document.getElementById(opts.formatId) : document.getElementById('format-select');
    _formatGetter = (typeof opts.getFormat === 'function') ? opts.getFormat : null;
    _labelEl  = _fieldEl ? _fieldEl.querySelector('label') : null;
    _toolName = opts.tool || (_selectEl && _selectEl.dataset.tool) || 'unknown';
    _preferSource = opts.preferSource === true;
    _maxWriterBits = (opts.maxWriterBits > 0) ? opts.maxWriterBits : 0;

    if (opts.lossyOptions)    _config.lossyOptions    = opts.lossyOptions.slice();
    if (opts.losslessOptions) _config.losslessOptions = opts.losslessOptions.slice();
    if (opts.flacOptions)     _config.flacOptions     = opts.flacOptions.slice();

    // Caller must provide EITHER a format <select> id OR a getFormat callback.
    // The callback path supports tools that use radio buttons / format-button
    // grids / dataset-driven UIs (reverb-echo, voice-changer, etc.) where
    // there's no underlying <select> to listen on. Those tools call
    // QualityModule.repopulate() manually after their format change.
    if (!_selectEl) return;
    if (!_formatEl && !_formatGetter) return;

    if (_formatEl) {
        _formatEl.addEventListener('change', repopulate);
    }
    repopulate();

    // Persist user choice across visits, scoped per tool. Skip if the host
    // tool already wired its own persistence (audio-enhancer's __toolSettings
    // call from the inline script will overwrite this fine).
    if (window.__toolSettings && _toolName && _toolName !== 'unknown') {
        try {
            window.__toolSettings.bind(_toolName, _selectEl, 'quality', _selectEl.value);
        } catch(_){}
        // bind() RESTORES a persisted value, and it runs AFTER repopulate()'s
        // gate — so a 320 kbps choice saved before the ceiling existed (or
        // while the user still had Premium) would land back in the select
        // un-gated and silently ship at 320. Re-gate here so that restore is
        // caught and disclosed like any other out-of-reach selection.
        applyQualityTier();
    }

    // The tier snapshot arrives from an async fetch, usually AFTER init(). Until
    // it lands __qualityTier fails open, so the first gate above is a no-op for
    // most page loads — this listener is what actually applies the ceiling. It
    // also un-gates for a Premium user whose plan resolved late.
    document.addEventListener('tier-bootstrap:ready', applyQualityTier);
}

// ── Stateless helpers for multi-instance use ─────────────────────────────
// The singleton init/getArgs path is one selector pair per page. Tools that
// render N quality selects (dictaphone — one per recording card; convert —
// per-file rows) can't share that state. populateOptions + argsFor expose
// the same kbps/bit-depth logic without any module-level binding: caller
// passes the element and the current ext directly.

function populateOptions(selectEl, ext, opts) {
    if (!selectEl) return;
    opts = opts || {};
    var conf = {
        lossyOptions:    opts.lossyOptions    || _config.lossyOptions,
        losslessOptions: opts.losslessOptions || _config.losslessOptions,
        flacOptions:     opts.flacOptions     || _config.flacOptions
    };
    var prev = selectEl.value;
    var lossless = isLossless(ext);
    var options;
    if (lossless) {
        var bits = isFlac(ext) ? conf.flacOptions : conf.losslessOptions;
        // Don't OFFER a depth this tool's export path cannot write — the plan
        // ceiling below shows premium depths disabled (that is a reason to
        // upgrade), but a tool whose WAV writer tops out at 16-bit simply has
        // nothing to sell there: picking 24 would relabel 16-bit data.
        var writerCeil = (opts.maxWriterBits > 0) ? opts.maxWriterBits : _maxWriterBits;
        if (writerCeil > 0) bits = bits.filter(function(b){ return b <= writerCeil; });
        options = bits.map(function(b){
            return { value: String(b), label: tr(b === 32 ? 'bit_n_float' : 'bit_n', { n: b }) };
        });
    } else {
        options = conf.lossyOptions.map(function(k){
            return { value: String(k), label: k + ' kbps' };
        });
    }
    var sourceLabel = opts.sourceLabel || tr('source');
    var html = '<option value="source">' + sourceLabel + '</option>';
    options.forEach(function(o) {
        html += '<option value="' + o.value + '">' + o.label + '</option>';
    });
    selectEl.innerHTML = html;

    /* Метка и подсказка — часть ЭТОГО же переключения, а не украшение.
     *
     * Однофайловый repopulate() свою метку меняет («Качество» ⇄ «Разрядность»),
     * а stateless-близнец не менял ничего: /convert показывал список
     * «16-бит / 24-бит / 32-бит float» под словом «Битрейт» с подсказкой про
     * kbps. Пользователь #1521 (30.08.2026) описал выбранную им опцию как
     * «25 bit» — из списка, подписанного битрейтом, глубина читается как
     * что-то другое. Слово знает модуль (11 локалей), поэтому его и отдаём —
     * тулу не нужен свой перевод.
     */
    if (opts.labelEl) {
        try { opts.labelEl.textContent = tr(lossless ? 'bit_depth' : 'quality'); } catch (_) {}
    }
    if (lossless) {
        try {
            var tip = tr('depth_tip');
            selectEl.title = tip;
            if (opts.labelEl && opts.labelEl.parentNode) opts.labelEl.parentNode.title = tip;
        } catch (_) {}
    } else if (opts.lossyTip !== undefined) {
        try {
            selectEl.title = opts.lossyTip;
            if (opts.labelEl && opts.labelEl.parentNode) opts.labelEl.parentNode.title = opts.lossyTip;
        } catch (_) {}
    }

    var validValues = ['source'].concat(options.map(function(o){return o.value;}));
    if (validValues.indexOf(prev) !== -1) {
        selectEl.value = prev;
    } else if (opts.preferredDefault && validValues.indexOf(String(opts.preferredDefault)) !== -1) {
        selectEl.value = String(opts.preferredDefault);
    } else {
        selectEl.value = lossless ? '16' : '192';
    }

    // Same ceiling as the singleton path. This branch matters more than it
    // looks: it is the one used by the MULTI-select tools (convert per-file
    // rows, dictaphone per-recording cards), where a missed gate would leak
    // full quality on every row while the single-select tools were capped.
    if (window.__qualityTier) {
        try {
            // Stamp the kind on the element so the SHARED passes (bootstrap
            // event, the late timer, a repopulate observer) can re-gate it with
            // the RIGHT kind later — the ceiling arrives asynchronously, and a
            // multi-select row populated before it landed would otherwise stay
            // ungated for a logged-in user. Kept in sync on every populate,
            // because the kind flips with the format (bits ⇄ kbps).
            selectEl.setAttribute('data-quality-gate', lossless ? 'bits' : 'kbps');
            window.__qualityTier.gate(selectEl, lossless ? 'bits' : 'kbps');
        } catch (_) {}
    }
}

// Stateless args producer. Caller passes ext + value directly so multiple
// concurrent <select>s can each compute their own args without cross-talk.
/**
 * The bit depth a lossless export will REALLY be written at: the user's pick
 * (or the source's own depth when they left it on "Source"), clamped by the
 * plan ceiling. Tools need this as a NUMBER — the WAV writer has to produce
 * those bits before FFmpeg ever sees the data, otherwise `-sample_fmt s32`
 * is just re-labelling audio whose resolution was already thrown away.
 * @returns {number} 16 | 24 | 32, or 0 for lossy containers
 */
/**
 * ⚠️ МНОГОФАЙЛОВЫЙ ТУЛ НЕ СПРАШИВАЕТ ЗДЕСЬ ПРО «ИСХОДНОЕ» — И НЕ ДОЛЖЕН.
 *
 * `_detectedSource` — синглтон: его заполняет detectSource() однофайлового
 * init(). У /convert в очереди файлы РАЗНОЙ разрядности, а место для ответа
 * одно, поэтому «Исходное» там разрешает СВОЙ план (buildPlan знает
 * `f.src.bits` из AudioHeaderProbe) и передаёт сюда уже число. Так у глубины
 * остаётся ОДИН владелец: план решает, модуль переводит решение в аргументы
 * FFmpeg. Обратный порядок — модуль решает за тул — и был корнем дефекта
 * 2026-09-03: /convert не мог сказать про свой файл ничего, обходил модуль
 * стороной для «Исходного», и Премиум получал 16 бит из 24-битного FLAC.
 */
function resolvedBits(ext, value, maxWriterBits, sourceBits) {
    if (!isLossless(ext)) return 0;
    var v = (value === undefined) ? getCurrentValue() : value;
    var isSource = (v === 'source' || v == null || v === '');
    // "Source" means the source's depth — it used to hard-code 16, so a
    // 24-bit file exported at the default lost 8 bits even on a plan whose
    // ceiling allowed 24, while the option's own label read "Source · 24-bit".
    // sourceBits names THAT source when it is not the file detectSource() saw
    // (a batch: every track brings its own depth — probeSource()).
    var srcBits = (sourceBits > 0) ? sourceBits : _detectedSource.bitDepth;
    var bits = isSource ? (srcBits || 16) : (parseInt(v, 10) || 16);
    // The <select> may hold a KBPS value belonging to a LOSSY format — either a
    // stale pick before the option list is rebuilt, or a tool whose per-item
    // format differs from the one driving the selector (/vocal-remover exports
    // FLAC stems while the selector sits on "320"). Reading "320" as a depth
    // made getBitDepthArgs ask FFmpeg for s32/24-bit over 16-bit data: a label,
    // not resolution. It is also not a request for 16-bit — the user never
    // picked a depth — so fall back to the SOURCE depth, same as "Source".
    if ([16, 24, 32].indexOf(bits) === -1) {
        /* Два разных «не из списка», и раньше они лечились одинаково — в 16.
         *
         * > 32 — это КБИТ/С, затесавшиеся из lossy-списка (документированный
         *   случай /vocal-remover: «320» читалось как глубина). Здесь глубину
         *   не просили вовсе, значит источник.
         * ≤ 32 — это настоящая глубина из заголовка файла, которой просто нет
         *   в нашем списке: 8, 20, 12. Такую надо ОКРУГЛИТЬ ВВЕРХ до
         *   ближайшей, которую мы умеем писать: 20 → 24 сохраняет все биты,
         *   20 → 16 четыре из них выбрасывает. Понижение обязано быть
         *   осознанным выбором, а не следствием того, что число не совпало
         *   с элементом массива.
         */
        if (bits > 32) bits = srcBits || 16;
        else if (bits > 24) bits = 32;
        else if (bits > 16) bits = 24;
        else bits = 16;
    }
    bits = capBits(bits);
    // Never ask the encoder for more resolution than the caller can actually
    // hand it. A tool whose WAV writer emits 16-bit would otherwise produce a
    // file LABELLED 24-bit carrying 16-bit data — `-sample_fmt s32` relabels,
    // it cannot restore. Tools declare what they can write via
    // init({maxWriterBits}); the default is open, since tools that feed FFmpeg
    // the ORIGINAL file (no intermediate render) lose nothing.
    var ceiling = (maxWriterBits > 0) ? maxWriterBits : _maxWriterBits;
    if (ceiling > 0 && bits > ceiling) bits = ceiling;
    return bits;
}

function argsFor(ext, value, maxWriterBits) {
    var fp = window.FFmpegProcessor;
    var isSource = (value === 'source' || value == null || value === '');
    if (isLossless(ext)) {
        return getBitDepthArgs(ext, resolvedBits(ext, value, maxWriterBits));   // per-row ceiling
    }
    if (isSource) {
        var ceil = audioKbpsCeiling();
        if (ceil !== Infinity && fp && fp.getCodecArgs) return fp.getCodecArgs(ext, ceil);
        return fp && fp.getCodecArgs ? fp.getCodecArgs(ext) : [];
    }
    var kbps = parseInt(value, 10);
    if (isNaN(kbps) || kbps <= 0) {
        return fp && fp.getCodecArgs ? fp.getCodecArgs(ext) : [];
    }
    return fp && fp.getCodecArgs ? fp.getCodecArgs(ext, capKbps(kbps)) : ['-b:a', capKbps(kbps) + 'k'];
}

window.QualityModule = {
    init: init,
    getArgs: getArgs,
    getCurrentValue: getCurrentValue,
    isLossless: isLossless,
    repopulate: repopulate,
    detectSource: detectSource,
    getDetectedSource: function() { return Object.assign({}, _detectedSource); },
    /* Did the last export lower the user's OWN bitrate to the tier ceiling?
       Answers {src, out, top, text} or null. The sentence is built by
       __qualityTier so every tool says it the same way, in ten locales. */
    lastSourceClamp: function() { return _lastSourceClamp; },
    /* Declare that this tool renders the downgrade sentence itself. */
    ownsSourceClampNotice: function() { _ownsClampNotice = true; },
    /**
     * Declare, at runtime, the deepest bit depth this tool's export path can
     * actually WRITE. Tools whose writer is the shared AudioPipeline call this
     * after awaiting ensureAudioPipeline(): if that load failed, their local
     * fallback writer emits 16-bit, and promising 24 would put 16-bit data
     * under a 24-bit header (measured on /pitch and /vocal-remover before this
     * existed: low byte zero on 100% of samples).
     * @param {number} bits 0 = no limit
     */
    setWriterCeiling: function(bits) { _maxWriterBits = (bits > 0) ? bits : 0; },
    populateOptions: populateOptions,  // stateless multi-instance helper
    argsFor: argsFor,                  // stateless args producer
    resolvedBits: resolvedBits,        // bit depth the export will really use
    /* Stateless twin of detectSource() for ANY file — {bitDepth, isFloat} from
       a WAV fmt chunk or FLAC STREAMINFO, null otherwise. Reads ≤96 bytes. A
       batch asks it per track; detectSource() stays the editor's own file. */
    probeSource: function(file) {
        var ext = ((file && file.name || '').split('.').pop() || '').toLowerCase();
        if (ext === 'wav' || ext === 'wave') return probeWavHeader(file);
        if (ext === 'flac') return probeFlacHeader(file);
        return Promise.resolve(null);
    },
    version: '1.3.0'
};

if (typeof window.__drainSharedModule === 'function') window.__drainSharedModule('QualityModule');

})();
